import React, { useEffect, useMemo, useState } from 'react';
import { Download, Phone, Mail, Users, Loader2, UserCheck, Check, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import Button from '../ui/Button';
import { Sheet } from './Sheet';
import { api } from '../../services/api';
import { cn } from '../../lib/utils';

export default function RegistrationsSheet({ event, onClose }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(-1);
  const [exporting, setExporting] = useState(null); // 'csv' | 'xlsx'

  useEffect(() => {
    api.getRegistrations(event.id).then((r) => setRows(r.map((x) => ({ ...x }))));
  }, [event.id]);

  const presentCount = useMemo(() => (rows || []).filter((r) => r.checkedIn).length, [rows]);
  const fields = Array.isArray(event.formFields) ? event.formFields : [];

  async function toggle(index) {
    if (busy !== -1) return;
    setBusy(index);
    const next = !rows[index].checkedIn;
    try {
      await api.setCheckIn(event.id, index, next);
      setRows((prev) => prev.map((r, i) => (i === index ? { ...r, checkedIn: next } : r)));
      toast.success(next ? 'Presença confirmada.' : 'Presença removida.');
    } catch {
      toast.error('Não foi possível atualizar a presença.');
    } finally {
      setBusy(-1);
    }
  }

  async function doExport(format) {
    if (!rows || rows.length === 0) {
      toast.error('Nenhum inscrito para exportar.');
      return;
    }
    setExporting(format);
    try {
      await api.exportRegistrations(event.id, format);
      toast.success(format === 'xlsx' ? 'Planilha exportada!' : 'Lista exportada!');
    } catch {
      toast.error('Não foi possível exportar.');
    } finally {
      setExporting(null);
    }
  }

  return (
    <Sheet title="Inscritos & Check-in" subtitle={event.title} onClose={onClose} testid="registrations-sheet">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-ink-soft" data-testid="regs-total">
            <Users size={14} /> {rows ? rows.length : '–'}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700" data-testid="regs-present">
            <UserCheck size={14} /> {presentCount} presentes
          </span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="accent" onClick={() => doExport('csv')} disabled={exporting !== null} data-testid="registrations-export">
            {exporting === 'csv' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Lista
          </Button>
          <Button size="sm" variant="outline" onClick={() => doExport('xlsx')} disabled={exporting !== null} data-testid="registrations-export-xlsx">
            {exporting === 'xlsx' ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />} Planilha
          </Button>
        </div>
      </div>

      {!rows ? (
        <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center" data-testid="registrations-empty">
          <Users size={26} className="mx-auto text-ink-faint" />
          <p className="mt-2 font-heading font-semibold text-ink">Nenhum inscrito ainda</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="registrations-list">
          {rows.map((r, i) => (
            <li
              key={i}
              className={cn(
                'flex items-center gap-3 rounded-xl border bg-white p-3 transition-colors duration-200',
                r.checkedIn ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200'
              )}
              data-testid={`registration-row-${i}`}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-heading font-bold text-ink">{r.name}</p>
                <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-soft">
                  <span className="inline-flex items-center gap-1"><Phone size={12} /> {r.phone || '—'}</span>
                  {r.email && <span className="inline-flex items-center gap-1"><Mail size={12} /> {r.email}</span>}
                  {fields.map((f) => {
                    const v = (r.answers || {})[f.key];
                    if (v === undefined || v === null || v === '') return null;
                    const label = Array.isArray(v) ? v.join(', ') : String(v);
                    return (
                      <span key={f.key} className="inline-flex items-center gap-1">
                        <span className="font-semibold text-ink">{f.label}:</span> {label}
                      </span>
                    );
                  })}
                </div>
              </div>
              <button
                onClick={() => toggle(i)}
                disabled={busy === i}
                data-testid={`checkin-toggle-${i}`}
                aria-label={r.checkedIn ? 'Remover presença' : 'Marcar presença'}
                className={cn(
                  'flex h-10 shrink-0 items-center gap-1.5 rounded-full px-3 font-heading text-xs font-semibold transition-colors duration-200',
                  r.checkedIn
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'border border-slate-200 bg-white text-ink-soft hover:border-emerald-400 hover:text-emerald-600'
                )}
              >
                {busy === i ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {r.checkedIn ? 'Presente' : 'Check-in'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}
